import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    Clock,
    Monitor,
    Search,
    X,
} from "lucide-react";
import CloudEntityActivityLogs from "./CloudEntityActivityLogs";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { useQuery } from "react-query";
import { formatTimeAgo } from "../formatTimeAgo";
import { getCloudAccountDisplayName } from "../cloudAccountSelection";

const activityGridColumns = "90px minmax(0, 2fr) minmax(0, 1.3fr) minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1fr)";

const isCloudTrailLog = (log: any, cloudType: string) =>
    (log?.cloudType || cloudType || "").toLowerCase() === "aws" && !log?.rawLog?.auditID;

const deriveAwsLogFields = (log: any) => {
    const raw = log?.rawLog || {};
    const userIdentity = raw.userIdentity || {};
    const requestParameters = raw.requestParameters || {};

    const derivedAction = raw.eventName
        ? `${raw.eventSource || ""}:${raw.eventName}`.replace(/^:/, "")
        : log.action;

    const clean = (val: any) => {
        if (!val) return "—";
        if (typeof val === "string" && val.toLowerCase() === "unknown") return "—";
        return val;
    };

    const resourceFromResources = Array.isArray(raw.resources) && raw.resources.length > 0
        ? (raw.resources[0].ARN || raw.resources[0].arn || raw.resources[0].resourceName || raw.resources[0])
        : null;

    const resourceFromRequest =
        requestParameters.bucketName ||
        requestParameters.queueUrl ||
        requestParameters.topicArn ||
        requestParameters.functionName ||
        requestParameters.tableName ||
        requestParameters.streamName ||
        requestParameters.logGroupName ||
        requestParameters.resourceArn ||
        requestParameters.resourceName ||
        requestParameters.resourceId ||
        requestParameters.distributionId ||
        requestParameters.dbClusterIdentifier ||
        requestParameters.dbInstanceIdentifier ||
        requestParameters.userName ||
        requestParameters.roleName ||
        requestParameters.policyArn ||
        requestParameters.groupName ||
        requestParameters.targetGroupArn ||
        requestParameters.certificateArn ||
        requestParameters.secretId ||
        requestParameters.secretArn ||
        requestParameters.keyId ||
        requestParameters.keyArn ||
        requestParameters.alarmName ||
        requestParameters.alarmArn ||
        requestParameters.identityPoolId ||
        requestParameters.trailARN ||
        requestParameters.trailName;

    const resourceDisplay = clean(
        resourceFromRequest ||
        (resourceFromResources === userIdentity.arn ? null : resourceFromResources) ||
        raw.eventSource ||
        log.cloudResourceType ||
        log.entityAssociated ||
        log.entity
    );

    return {
        action: clean(derivedAction || log.action),
        resourceDisplay,
        entityDisplay: clean(userIdentity.userName || userIdentity.type || userIdentity.arn),
        sourceIp: clean(raw.sourceIPAddress || (Array.isArray(log.sourceIp) ? log.sourceIp[0] : log.sourceIp)),
        accountId: clean(userIdentity.accountId || log.cloudId),
        timestamp: clean(raw.eventTime || log.logTimestamp),
        service: clean(raw.eventSource || log.cloudResourceType || "AWS"),
    };
};


export default function CloudRecentActivity({
    url,
    tenantName,
    cloudType,
    subscriptionName,
    resourceAnalytics = false,
}: {
    url: string;
    tenantName: string;
    cloudType: string;
    subscriptionName?: string;
    resourceAnalytics?: boolean;
}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedAlerts, setExpandedAlerts] = useState<Set<number>>(new Set());
    const itemsPerPage = 12;
    const isAzure = cloudType.toUpperCase() === "AZURE";

    const {
        data: activity = [],
        isLoading,
    } = useQuery({
        queryKey: ["activity", tenantName, cloudType, url],
        queryFn: async () => {
            const { data } = await axios.get(url);
            return Array.isArray(data) ? data : [];
        },
    });

    const filteredActivity = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return activity;

        return activity.filter((log: any) => {
            const isAWS = isCloudTrailLog(log, cloudType);
            const awsDerived = isAWS ? deriveAwsLogFields(log) : null;
            const cloudAccountId = isAWS ? awsDerived?.accountId : log.cloudId;
            const cloudAccountName = isAzure
                ? subscriptionName || getCloudAccountDisplayName(cloudType, cloudAccountId)
                : getCloudAccountDisplayName(cloudType, cloudAccountId);
            const timestamp = isAWS ? awsDerived?.timestamp : log.logTimestamp;
            const level = log.logType?.toLowerCase() === "notice" ? "high" : log.logType || "info";
            const sourceIp = isAWS ? awsDerived?.sourceIp : log.sourceIp;

            return [
                level,
                isAWS ? awsDerived?.service : log.entity,
                timestamp,
                formatTimeAgo(timestamp),
                isAWS ? awsDerived?.action : log.action,
                log.cloudResourceType,
                isAWS ? awsDerived?.resourceDisplay : log.entityAssociated,
                cloudAccountName,
                cloudAccountId,
                sourceIp,
            ].some((value) => String(value ?? "").toLowerCase().includes(query));
        });
    }, [activity, cloudType, isAzure, searchQuery, subscriptionName]);

    const totalPages = Math.ceil(filteredActivity.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedLogs = filteredActivity.slice(startIndex, startIndex + itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [url]);

    useEffect(() => {
        setCurrentPage((page) => Math.min(page, Math.max(totalPages, 1)));
    }, [totalPages]);

    const getSeverityBadge = (level: string = "info") => {
        switch (level?.toLowerCase()) {
            case "info":
                return (
                    <Badge className="text-xs text-white bg-blue-500 hover:bg-blue-600">
                        {level}
                    </Badge>
                );
            case "notice":
                return (
                    <Badge className="text-xs text-white bg-orange-500 hover:bg-orange-600">
                        High
                    </Badge>
                );
            case "error":
                return (
                    <Badge variant={"destructive"}>
                        {level}
                    </Badge>
                );
            case "warn":
                return (
                    <Badge className="text-xs text-white bg-yellow-500 hover:bg-yellow-600">
                        {level}
                    </Badge>
                );
            case "low":
                return (
                    <Badge className="text-xs text-white bg-blue-500 hover:bg-blue-600">
                        Low
                    </Badge>
                );
            default:
                return (
                    <Badge variant="secondary" className="text-xs">
                        INFO
                    </Badge>
                );
        }
    };

    const toggleAlertExpansion = (id: number) => {
        const newExpanded = new Set(expandedAlerts);
        newExpanded.has(id) ? newExpanded.delete(id) : newExpanded.add(id);
        setExpandedAlerts(newExpanded);
    };



    const getPageNumbers = (currentPage: number, totalPages: number) => {
        const delta = 2;
        const range: (number | string)[] = [];
        const rangeWithDots: (number | string)[] = [];
        let last: number | null = null;
        for (let i = 1; i <= totalPages; i++) {
            if (
                i === 1 ||
                i === totalPages ||
                (i >= currentPage - delta && i <= currentPage + delta)
            ) {
                range.push(i);
            }
        }
        for (let i of range) {
            if (last) {
                if ((i as number) - last === 2) rangeWithDots.push(last + 1);
                else if ((i as number) - last !== 1) rangeWithDots.push("…");
            }
            rangeWithDots.push(i);
            last = i as number;
        }
        return rangeWithDots;
    };

    const renderSkeletonRow = (key: number) => (
        <div
            key={key}
            className="grid gap-4 px-3 py-3 text-sm animate-pulse"
            style={{ gridTemplateColumns: activityGridColumns }}
        >
            <div className="w-20 h-6 bg-gray-300 rounded" />
            <div className="w-full h-6 bg-gray-300 rounded" />
            <div className="w-full h-6 bg-gray-300 rounded" />
            <div className="w-16 h-6 bg-gray-300 rounded" />
            <div className="w-16 h-6 bg-gray-300 rounded" />
            <div className="w-16 h-6 bg-gray-300 rounded" />
        </div>
    );

    return (
        <Card className="cea-card-surface cloud-card-surface rounded-2xl min-h-[calc(100vh-280px)] flex flex-col">
            <CardHeader className="flex flex-col gap-3 space-y-0 md:flex-row md:items-center">
                <div className="group relative min-w-0 flex-1">
                    <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-primary" />
                    <Input
                        value={searchQuery}
                        onChange={(event) => {
                            setSearchQuery(event.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Search all activity logs..."
                        aria-label="Search all recent cloud activities"
                        autoComplete="off"
                        spellCheck={false}
                        className="h-10 rounded-xl border-slate-200 bg-white pl-11 pr-10 text-sm text-slate-900 shadow-[0_2px_8px_rgba(15,23,42,0.07)] placeholder:text-slate-400 hover:border-primary/30 focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:border-slate-200 dark:bg-white dark:text-slate-900"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchQuery("");
                                setCurrentPage(1);
                            }}
                            aria-label="Clear activity search"
                            className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                {!isLoading && filteredActivity.length > 0 && (
                    <div className="flex shrink-0 items-center justify-end gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-1 h-7"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        {getPageNumbers(currentPage, totalPages).map((p, i) =>
                            typeof p === "number" ? (
                                <Button
                                    key={i}
                                    variant={currentPage === p ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setCurrentPage(p)}
                                    className="h-7 min-w-7 text-xs px-2"
                                >
                                    {p}
                                </Button>
                            ) : (
                                <span key={i} className="px-1 text-xs text-muted-foreground">
                                    …
                                </span>
                            )
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="p-1 h-7"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                )}
            </CardHeader>

            <CardContent className="flex-1 flex flex-col">
                <div className="space-y-4 flex-1 flex flex-col">
                    <div
                        className="grid gap-4 px-3 py-2.5 text-[11px] font-semibold tracking-wide uppercase rounded-[10px] border border-border shadow-[0_2px_8px_rgb(15_23_42/0.06)] text-muted-foreground"
                        style={{
                            gridTemplateColumns: activityGridColumns,
                        }}
                    >
                        <div>Level</div>
                        <div>{resourceAnalytics ? "Resource" : "Details"}</div>
                        <div>Action</div>
                        <div>{resourceAnalytics ? "Entity" : "Resource"}</div>
                        <div>{isAzure ? "Subscription" : cloudType.toUpperCase() === "AWS" ? "Account" : "Project"}</div>
                        <div>Source IP</div>
                    </div>
                    {isLoading && (
                        <div className="space-y-1">
                            {[...Array(itemsPerPage)].map((_, idx) => renderSkeletonRow(idx))}
                        </div>
                    )}
                    {!isLoading && activity.length === 0 && (
                        <div className="py-8 text-sm text-center text-muted-foreground">
                            No activity logs found.
                        </div>
                    )}
                    {!isLoading && activity.length > 0 && filteredActivity.length === 0 && (
                        <div className="py-8 text-sm text-center text-muted-foreground">
                            No activity logs match your search.
                        </div>
                    )}


                    {!isLoading &&
                        paginatedLogs.map((log: any) => {
                            const isExpanded = expandedAlerts.has(log.id);
                            const isAWS = isCloudTrailLog(log, cloudType);
                            const awsDerived = isAWS ? deriveAwsLogFields(log) : null;
                            const cloudAccountId = isAWS ? awsDerived?.accountId : log.cloudId;
                            const cloudAccountName = isAzure
                                ? subscriptionName || getCloudAccountDisplayName(cloudType, cloudAccountId)
                                : getCloudAccountDisplayName(cloudType, cloudAccountId);

                            return (
                                <div
                                    key={log.id}
                                    className="cea-inner-surface cloud-list-card rounded-2xl overflow-hidden"
                                >
                                    <div
                                        className="grid items-start gap-4 px-3 py-3 text-sm cursor-pointer"
                                        style={{
                                            gridTemplateColumns: activityGridColumns,
                                        }}
                                        onClick={() => toggleAlertExpansion(log.id)}
                                    >
                                        {/* Level Section */}
                                        <div className="flex items-center gap-2">
                                            {isExpanded ? (
                                                <ChevronUp className="w-4 h-4" />
                                            ) : (
                                                <ChevronDown className="w-4 h-4" />
                                            )}
                                            {getSeverityBadge(log.logType)}
                                        </div>

                                        {/* Details Section */}
                                        <div className="min-w-0 space-y-1">
                                            <div className="flex items-center gap-1 text-xs text-foreground">
                                                <Monitor className="w-3 h-3 text-muted-foreground" />
                                                <span className="truncate" title={isAWS ? awsDerived?.service : log.entity}>
                                                    {isAWS ? awsDerived?.service : log.entity}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <Clock className="w-3 h-3" />
                                                <span>{formatTimeAgo(isAWS ? awsDerived?.timestamp : log.logTimestamp)}</span>
                                            </div>
                                        </div>

                                        {/* Action Section */}
                                        <div className="min-w-0 space-y-1">

                                            <div className="font-semibold text-foreground text-xs text-muted-foreground break-all whitespace-pre-wrap" title={isAWS ? awsDerived?.action : log.action}>
                                                {isAWS ? awsDerived?.action : log.action}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground" title={log.cloudResourceType}>
                                                {log.cloudResourceType}
                                            </div>
                                        </div>

                                        {/* Resource Section */}
                                        <div className="min-w-0">
                                            <div className="text-xs break-words text-foreground" title={isAWS ? awsDerived?.resourceDisplay : log.entityAssociated || "—"}>
                                                {isAWS ? awsDerived?.resourceDisplay : log.entityAssociated || "—"}
                                            </div>
                                        </div>

                                        {/* Project Section */}
                                        <div className="min-w-0">
                                            <Badge className="max-w-full bg-gray-500 text-white text-xs px-2" title={cloudAccountName}>
                                                <span className="truncate">{cloudAccountName}</span>
                                            </Badge>
                                        </div>

                                        {/* Source IP Section */}
                                        <div className="min-w-0">
                                            <div className="text-xs break-all text-foreground" title={(isAWS ? awsDerived?.sourceIp : log.sourceIp) || "—"}>
                                                {(isAWS ? awsDerived?.sourceIp : log.sourceIp) || "—"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Tree Viewer */}
                                    {isExpanded && (
                                        <CloudEntityActivityLogs data={log.rawLog} />
                                    )}
                                </div>

                            );
                        })}
                    {/* Spacer to absorb empty space */}
                    <div className="flex-1" />
                </div>
            </CardContent>
        </Card>


    );
}
